import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { thinkingFrameworkService, ThinkingFrameworkSession } from '../../services/thinkingFrameworkService';
import PageHeader from '../common/PageHeader';
import './ThinkingFrameworks.css';

interface ThinkingFrameworksProps {
  onToggleSidebar: () => void;
}

interface FrameworkInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
}

const FRAMEWORKS: FrameworkInfo[] = [
  {
    id: 'swot',
    name: 'SWOT Analysis',
    description: 'Analyze Strengths, Weaknesses, Opportunities, and Threats',
    icon: '📊'
  },
  {
    id: '5whys',
    name: '5 Whys',
    description: 'Determine the root cause of a problem by repeating the question "Why?"',
    icon: '❓'
  },
  {
    id: 'first_principles',
    name: 'First Principles',
    description: 'Break problems down to basic truths and rebuild from there',
    icon: '🧱'
  },
  {
    id: 'second_order',
    name: 'Second-Order Thinking',
    description: 'Consider the long-term consequences of your decisions',
    icon: '🌊'
  }
];

const ThinkingFrameworks: React.FC<ThinkingFrameworksProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Record<string, ThinkingFrameworkSession[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadSessions();
    }
  }, [user]);

  const loadSessions = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const allSessions = await thinkingFrameworkService.getSessions();
      
      // Group sessions by framework type
      const grouped: Record<string, ThinkingFrameworkSession[]> = {};
      allSessions.forEach(session => {
        if (!grouped[session.framework_type]) {
          grouped[session.framework_type] = [];
        }
        grouped[session.framework_type].push(session);
      });
      
      setSessions(grouped);
    } catch (err) {
      console.error('Error loading thinking framework sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = (frameworkType: string) => {
    navigate(`/thinking-frameworks/${frameworkType}`);
  };

  const handleOpenSession = (sessionId: string, frameworkType: string) => {
    navigate(`/thinking-frameworks/${frameworkType}/${sessionId}`);
  };

  const handleDeleteSession = async (e: React.MouseEvent, sessionId: string, frameworkType: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      setError(null);
      await thinkingFrameworkService.deleteSession(sessionId);
      await loadSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="thinking-frameworks">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="thinking-frameworks-header-title">/thinking-frameworks</h1>}
      />
      
      <div className="thinking-frameworks-content">
        {error && (
          <div className="thinking-frameworks-error">{error}</div>
        )}

        {loading ? (
          <div className="thinking-frameworks-loading">Loading frameworks...</div>
        ) : (
          <div className="thinking-frameworks-grid">
            {FRAMEWORKS.map((framework) => {
              const frameworkSessions = sessions[framework.id] || [];
              
              return (
                <div key={framework.id} className="thinking-frameworks-card">
                  <div className="thinking-frameworks-card-header">
                    <div className="thinking-frameworks-card-icon">{framework.icon}</div>
                    <div className="thinking-frameworks-card-info">
                      <h2 className="thinking-frameworks-card-title">{framework.name}</h2>
                      <p className="thinking-frameworks-card-description">{framework.description}</p>
                    </div>
                  </div>
                  
                  <button
                    className="thinking-frameworks-create-btn"
                    onClick={() => handleCreateNew(framework.id)}
                  >
                    + New {framework.name}
                  </button>

                  {frameworkSessions.length > 0 && (
                    <div className="thinking-frameworks-sessions">
                      <h3 className="thinking-frameworks-sessions-title">Recent Sessions</h3>
                      <div className="thinking-frameworks-sessions-list">
                        {frameworkSessions.map((session) => (
                          <div
                            key={session.session_id}
                            className="thinking-frameworks-session-item"
                            onClick={() => handleOpenSession(session.session_id, framework.id)}
                          >
                            <div className="thinking-frameworks-session-info">
                              <span className="thinking-frameworks-session-title">{session.title}</span>
                              <span className="thinking-frameworks-session-date">
                                {formatDate(session.updated_at)}
                              </span>
                            </div>
                            <button
                              className="thinking-frameworks-session-delete"
                              onClick={(e) => handleDeleteSession(e, session.session_id, framework.id)}
                              title="Delete"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThinkingFrameworks;

